import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { combineKilogramsAndGrams } from 'ui-components/components/weight-input/helpers';
import { combinePoundsAndOunces } from 'ui-components/components/weight-input/ounces-weight-input';

import { appHeaderMessages } from '../../../../../components/app-header/messages';
import { getRateBrowserDefaults } from '../../../../../components/app-header/rate-browser-button';
import { useTrackRateBrowser } from '../../../../../components/rate-viewer/use-track-rate-browser';
import { SRC_GET_RATES_CLICKED, SRC_INPUT_CHANGED } from '../../../../../constants/analytics-events';
import { AU, GB, CA, US } from '../../../../../constants/country-codes';
import { DimensionUnit } from '../../../../../enums/dimension-unit';
import { RateViewerTypes } from '../../../../../enums/rate-viewer-types';
import { WeightUnit } from '../../../../../enums/weight-unit';
import { Segment } from '../../../../../services/analytics-services/segment';
import Global from '../../../../../store/global-selectors';
import { buildBrowseRatesRequestFromConfigureRatesModel } from '../../../../../store/rate-browser/rate-broswer-helpers';
import { openRateBrowserModal, updateConfigureRatesModel } from '../../../../../store/rate-browser/rate-browser.actions';
import { loadRates } from '../../../../../store/rate-viewer/rate-viewer.actions';
import { WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';

const defaultDimensions = {
  unit: DimensionUnit.Inches,
  length: 0,
  width: 0,
  height: 0,
};

const maxZipLength = 10; // Supports ZIP+4 format (12345-6789)
const DEFAULT_WEIGHT_LBS = 2;
const DEFAULT_WEIGHT_OZ = 0;
const DEFAULT_KG_LBS = 1;
const DEFAULT_G_OZ = 0;

const DefaultConfiguration = {
  [US]: {
    fromPostalCode: '60601',
    toPostalCode: '90405',
  },
  [GB]: {
    fromPostalCode: 'SW1A 0AA',
    toPostalCode: 'M1 2QF',
  },
  [AU]: {
    fromPostalCode: '2000',
    toPostalCode: '3000',
  },
  [CA]: {
    fromPostalCode: 'H3G 1P7',
    toPostalCode: 'V7Y 1G5',
  },
};

export const useShippingCalculator = (analyticsSource = 'HOME_PAGE_CALCULATOR') => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const trackRateBrowser = useTrackRateBrowser();
  const defaultRateBrowserValues = useSelector((state) => getRateBrowserDefaults(state));
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const defaultConfig = DefaultConfiguration[sellerHomeCountry] || DefaultConfiguration[US];

  // Determine if we should use imperial (lb/oz) or metric (kg/g) units
  const isImperial = sellerWeightUnit === WeightUnit.Ounces || sellerWeightUnit === WeightUnit.Pounds;

  // Internal state for the shipping rate calculator
  const [originZip, setOriginZip] = useState(defaultConfig.fromPostalCode);
  const [destinationZip, setDestinationZip] = useState(defaultConfig.toPostalCode);

  const [weight, setWeight] = useState(() => {
    return isImperial
      ? combinePoundsAndOunces(DEFAULT_WEIGHT_LBS, DEFAULT_WEIGHT_OZ)
      : combineKilogramsAndGrams(DEFAULT_KG_LBS, DEFAULT_G_OZ);
  });

  const handleGetRates = useCallback(() => {
    let selectedPackage = defaultRateBrowserValues.selectedPackage;

    if (selectedPackage && selectedPackage.nameMessageId) {
      selectedPackage = {
        ...selectedPackage,
        name: intl.formatMessage(appHeaderMessages[selectedPackage.nameMessageId]),
      };
    }

    const currentWeightUnit = isImperial ? WeightUnit.Ounces : WeightUnit.Grams;
    const dimensions = selectedPackage?.dimensions || defaultDimensions;

    const rateBrowserDefaults = {
      ...defaultRateBrowserValues,
      selectedPackage,
    };

    const updatePayload = {
      shipFromId: defaultRateBrowserValues.shipFromId,
      shipFromPostalCode: originZip,
      shipToPostalCode: destinationZip,
      shipToCountryCode: sellerHomeCountry,
      weight: {
        weightUnit: currentWeightUnit,
        weight,
      },
      dimensions,
    };

    Segment.trackEvent(SRC_GET_RATES_CLICKED, {
      payload: {
        shipFromPostalCode: originZip,
        shipToPostalCode: destinationZip,
        weight: {
          weightUnit: currentWeightUnit,
          weight,
        },
      },
    });

    dispatch(openRateBrowserModal(rateBrowserDefaults));
    dispatch(updateConfigureRatesModel(updatePayload));

    // Start loading rates immediately when modal opens
    const rateRequest = buildBrowseRatesRequestFromConfigureRatesModel(updatePayload);
    dispatch(loadRates(RateViewerTypes.rateBrowser, rateRequest));

    trackRateBrowser(WorkflowSteps.START, { source: analyticsSource });
  }, [
    originZip,
    destinationZip,
    weight,
    dispatch,
    trackRateBrowser,
    defaultRateBrowserValues,
    isImperial,
    intl,
    analyticsSource,
    sellerHomeCountry,
  ]);

  const handleOriginZipChange = useCallback((e) => {
    const newValue = e.target.value;
    setOriginZip(newValue);

    Segment.trackEvent(SRC_INPUT_CHANGED, {
      inputType: 'originZip',
      newValue,
    });
  }, []);

  const handleDestinationZipChange = useCallback((e) => {
    const newValue = e.target.value;
    setDestinationZip(newValue);

    Segment.trackEvent(SRC_INPUT_CHANGED, {
      inputType: 'destinationZip',
      newValue,
    });
  }, []);

  const handleWeightChange = useCallback((newWeight) => {
    setWeight(newWeight);

    Segment.trackEvent(SRC_INPUT_CHANGED, {
      inputType: 'weight',
      newValue: newWeight,
    });
  }, []);

  return {
    // State
    originZip,
    destinationZip,
    weight,
    isImperial,

    // Handlers
    handleGetRates,
    handleOriginZipChange,
    handleDestinationZipChange,
    handleWeightChange,

    // Constants
    maxZipLength,
    intl,
  };
};
